import { Component, Inject, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectChange, MatSelectModule } from '@angular/material/select';
import { MatOptionModule } from '@angular/material/core';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';

import { Building } from '@app/shared/models/building.interface';
import { Role } from '@app/shared/models/role.interface';
import { User } from '@app/shared/models/user.interface';
import { UserService } from '@services/user/user.service';
import { RoleService } from '@services/role.service';

export interface AddUsersDialogData {
  buildings: Building[];
  managingCompanyId: number;
}

@Component({
  selector: 'app-managing-company-add-users-dialog',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatDialogModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    MatOptionModule,
    MatButtonModule,
    MatIconModule
  ],
  templateUrl: './managing-company-add-users-dialog.component.html',
  styleUrls: ['./managing-company-add-users-dialog.component.scss']
})
export class ManagingCompanyAddUsersDialogComponent implements OnInit {
  form: FormGroup;
  searching = false;
  showDetails = false;
  notFound = false;
  foundUser: User | null = null;
  roles: Role[] = [];

  constructor(
    private fb: FormBuilder,
    private dialogRef: MatDialogRef<ManagingCompanyAddUsersDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: AddUsersDialogData,
    private userService: UserService,
    private roleService: RoleService
  ) {
    this.form = this.fb.group({
      emailAddress: ['', [Validators.required, Validators.email]],
      buildingId: [null, [Validators.required]],
      roleId: [null, [Validators.required]]
    });
  }

  ngOnInit(): void {
    this.form.get('emailAddress')!.valueChanges.subscribe(() => {
      this.resetDetailsStep();
    });
  }

  onSearch(): void {
    const emailCtrl = this.form.get('emailAddress')!;
    emailCtrl.markAsTouched();
    emailCtrl.updateValueAndValidity();
    if (emailCtrl.invalid) return;

    this.searching = true;
    this.notFound = false;
    this.foundUser = null;
    this.showDetails = false;

    this.userService.findUserByEmail(emailCtrl.value, this.data.managingCompanyId).subscribe({
      next: (user) => {
        this.searching = false;

        if (!user || !user.id) {
          this.notFound = true;
          return;
        }

        this.foundUser = user;
        this.showDetails = true;
      },
      error: () => {
        this.searching = false;
        this.notFound = true;
      }
    });
  }

  private resetDetailsStep(): void {
    this.showDetails = false;
    this.notFound = false;
    this.foundUser = null;
    this.roles = [];
    this.form.patchValue({ buildingId: null, roleId: null }, { emitEvent: false });
    this.form.get('buildingId')!.markAsPristine();
    this.form.get('roleId')!.markAsPristine();
  }

  onBuildingChanged(event: MatSelectChange): void {
    const buildingId: number = event.value;
    if (!buildingId) {
      this.roles = [];
      this.form.patchValue({ roleId: null }, { emitEvent: false });
      return;
    }

    this.roleService.getAllForBuilding(buildingId).subscribe({
      next: (roles) => {
        this.roles = roles || [];
        this.form.patchValue({ roleId: null }, { emitEvent: false });
        this.form.get('roleId')!.updateValueAndValidity();
      },
      error: () => {
        this.roles = [];
        this.form.patchValue({ roleId: null }, { emitEvent: false });
      }
    });
  }

  close(): void {
    this.dialogRef.close();
  }

  save(): void {
    if (!this.foundUser) return;

    const { emailAddress, buildingId, roleId } = this.form.value as {
      emailAddress: string;
      buildingId: number;
      roleId: number;
    };

    if (!emailAddress || !buildingId || !roleId) return;

    this.dialogRef.close({
      user: this.foundUser,
      emailAddress,
      buildingId,
      roleId: roleId
    });
  }
}
